import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Bot, History, Loader2, MessageSquare, Plus, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const SUGGESTIONS = [
  "Fais le point sur les campagnes actives et les anomalies prioritaires.",
  "Où le budget est-il dépensé sans résultat ?",
  "Quelles campagnes faut-il optimiser cette semaine ?",
];

const profilAgent = {
  name: "Analyste Publicitaire",
  icon: "📣",
  color: "violet",
  mission: "Analyser les campagnes, les coûts, les prospects et proposer des améliorations publicitaires concrètes.",
  profile: "Expert acquisition orienté rentabilité, avec une attention particulière à La Réunion et aux données réellement mesurées. Tu compares les périodes, repères les budgets sans résultat et classes les actions par priorité.",
  memory: "Retenir les campagnes, audiences, visuels, résultats et arbitrages validés par Rudolph. Ne jamais mélanger les sociétés ni présenter une hypothèse comme un chiffre mesuré.",
};

const erreurDetail = async (response, fallback) => {
  try { return (await response.json()).detail || fallback; } catch { return fallback; }
};

export default function PubTrackingAgent({ ouvert, fermer, token, campagnes, comptes, veille, filtreCompte }) {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [agent, setAgent] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [evenements, setEvenements] = useState([]);
  const [saisie, setSaisie] = useState("");
  const [encours, setEncours] = useState(false);
  const filRef = useRef(null);

  const chargerConversations = useCallback(async (agentId) => {
    if (!agentId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/atelier/agents/${agentId}/conversations`, auth);
      setConversations(data || []);
    } catch { setConversations([]); }
  }, [auth]);

  const chargerAgent = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/atelier/agents`, auth);
      let analyse = (data || []).find((item) =>
        item.name?.toLocaleLowerCase("fr").includes("publicitaire"));
      if (!analyse) {
        const creation = await axios.post(`${API_URL}/api/atelier/agents`, profilAgent, auth);
        analyse = creation.data;
      }
      setAgent(analyse);
      await chargerConversations(analyse.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de charger l’agent publicitaire");
    } finally { setChargement(false); }
  }, [auth, chargerConversations]);

  useEffect(() => { if (ouvert && !agent) chargerAgent(); }, [ouvert, agent, chargerAgent]);
  useEffect(() => {
    if (filRef.current) filRef.current.scrollTop = filRef.current.scrollHeight;
  }, [evenements, encours]);

  const nouvelleConversation = () => { setSessionId(null); setEvenements([]); setSaisie(""); };

  const ouvrirConversation = async (id) => {
    if (!agent) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/atelier/agents/${agent.id}/conversations/${id}`, auth);
      setEvenements((data.messages || []).map((message) => ({
        type: message.role === "user" ? "user" : "assistant", texte: message.text,
      })));
      setSessionId(id);
    } catch { toast.error("Conversation introuvable"); }
  };

  const contexteDirect = () => {
    const selection = (campagnes || [])
      .filter((campagne) => filtreCompte === "tous" || campagne.compte_id === filtreCompte)
      .slice(0, 100)
      .map((campagne) => ({
        campagne: campagne.nom,
        compte: campagne.compte_nom,
        etat: campagne.etat,
        statut_meta: campagne.statut,
        objectif: campagne.objectif,
        depense_30j: campagne.depense || 0,
        impressions_30j: campagne.impressions || 0,
        clics_30j: campagne.clics || 0,
        ctr_30j: campagne.ctr || 0,
        cpc_30j: campagne.cpc || 0,
        resultats_30j: campagne.resultats || 0,
        type_resultat: campagne.resultat_libelle || null,
        cout_resultat_30j: campagne.cout_resultat || 0,
        budget_jour: campagne.budget_jour || null,
        debut: campagne.debut || null,
        fin: campagne.fin || null,
        recommandation_existante: campagne.recommandation || null,
      }));
    return [
      "CONTEXTE PUB STUDIO ACTUALISÉ — chiffres lus chez Meta sur les 30 derniers jours.",
      "Utilise ces données avant toute conclusion. Signale les données manquantes et ne mélange jamais les comptes.",
      `Filtre affiché : ${filtreCompte === "tous" ? "tous les comptes" : filtreCompte}.`,
      `Comptes : ${JSON.stringify((comptes || []).map((compte) => ({ nom: compte.nom, societe: compte.societe, depense_30j: compte.depense || 0 })))}`,
      `Alertes de veille : ${JSON.stringify(veille?.alertes || [])}`,
      `Campagnes (${selection.length}) : ${JSON.stringify(selection)}`,
    ].join("\n");
  };

  const traiter = (evt) => {
    if (evt.type === "done") {
      if (evt.session_id) setSessionId(evt.session_id);
      if (agent) chargerConversations(agent.id);
      return;
    }
    if (evt.type === "error") {
      setEvenements((liste) => [...liste, { type: "error", texte: evt.message }]);
      return;
    }
    if (evt.type === "assistant" && evt.message?.content) {
      evt.message.content.forEach((contenu) => {
        if (contenu.type === "text" && contenu.text) {
          setEvenements((liste) => [...liste, { type: "assistant", texte: contenu.text }]);
        }
      });
    }
  };

  const envoyer = async (message) => {
    const visible = message.trim();
    if (!visible || !agent || encours) return;
    setEncours(true); setSaisie("");
    setEvenements((liste) => [...liste, { type: "user", texte: visible }]);
    const prompt = `${contexteDirect()}\n\nDEMANDE DE RUDOLPH\n${visible}`;
    try {
      const response = await fetch(`${API_URL}/api/atelier/agents/${agent.id}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt, display_prompt: visible, resume: sessionId, file_ids: [] }),
      });
      if (!response.ok || !response.body) {
        throw new Error(await erreurDetail(response, "L’agent n’a pas pu démarrer"));
      }
      const lecteur = response.body.getReader();
      const decodeur = new TextDecoder();
      let tampon = "";
      while (true) {
        const { done, value } = await lecteur.read();
        if (done) break;
        tampon += decodeur.decode(value, { stream: true });
        const blocs = tampon.split("\n\n"); tampon = blocs.pop();
        blocs.forEach((bloc) => {
          if (!bloc.startsWith("data: ")) return;
          try { traiter(JSON.parse(bloc.slice(6))); } catch { /* fragment incomplet */ }
        });
      }
      if (tampon.trim().startsWith("data: ")) {
        try { traiter(JSON.parse(tampon.trim().slice(6))); } catch { /* dernier fragment incomplet */ }
      }
    } catch (error) {
      const detail = error.message || "Connexion avec l’agent interrompue";
      setEvenements((liste) => [...liste, { type: "error", texte: detail }]);
      toast.error(detail);
    } finally { setEncours(false); }
  };

  if (!ouvert) return null;

  return (
    <div className="fixed inset-0 z-[55] flex justify-end" onClick={fermer}>
      <div className="absolute inset-0 bg-black/25" />
      <section className="relative w-[540px] max-w-[96vw] h-full bg-white shadow-2xl flex flex-col"
               onClick={(event) => event.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 text-xl flex items-center justify-center">📣</div>
          <div className="min-w-0 flex-1">
            <h2 className="font-bold text-slate-900">Agent de suivi publicitaire</h2>
            <p className="text-[11px] text-slate-500">Campagnes Meta actualisées · lecture et recommandations</p>
          </div>
          <button onClick={fermer} className="p-2 text-slate-400 hover:text-slate-700" aria-label="Fermer"><X className="w-5 h-5" /></button>
        </header>

        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
          <History className="w-4 h-4 text-slate-400" />
          <select value={sessionId || ""} onChange={(event) => event.target.value ? ouvrirConversation(event.target.value) : nouvelleConversation()}
                  disabled={!agent || chargement}
                  className="flex-1 min-w-0 text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2">
            <option value="">Nouvelle discussion</option>
            {conversations.map((conversation) => (
              <option key={conversation.session_id} value={conversation.session_id}>{conversation.title}</option>
            ))}
          </select>
          <Button size="icon" variant="outline" onClick={nouvelleConversation} title="Nouvelle discussion">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div ref={filRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {chargement ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Préparation de l’agent…</div>
          ) : !evenements.length ? (
            <div className="max-w-md mx-auto text-center py-12">
              <Bot className="w-11 h-11 text-violet-200 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800">Votre analyste publicitaire est prêt</h3>
              <p className="text-sm text-slate-500 mt-2">Il reçoit les chiffres actuellement lus chez Meta et conserve son propre historique.</p>
              <div className="grid gap-2 mt-5 text-left">
                {SUGGESTIONS.map((suggestion) => (
                  <button key={suggestion} onClick={() => envoyer(suggestion)}
                          className="text-xs border border-slate-200 hover:border-violet-300 hover:bg-violet-50 rounded-lg px-3 py-2.5 text-slate-700">
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : evenements.map((evenement, index) => evenement.type === "user" ? (
            <div key={index} className="flex justify-end"><div className="max-w-[88%] bg-violet-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm whitespace-pre-wrap">{evenement.texte}</div></div>
          ) : evenement.type === "assistant" ? (
            <div key={index} className="max-w-[95%] bg-slate-50 border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap">{evenement.texte}</div>
          ) : (
            <div key={index} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{evenement.texte}</div>
          ))}
          {encours && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Analyse des campagnes en cours…</div>}
        </div>

        <footer className="border-t border-slate-200 p-3">
          <div className="flex gap-2 items-end">
            <Textarea rows={2} value={saisie} onChange={(event) => setSaisie(event.target.value)}
                      disabled={encours || !agent} placeholder="Demandez une analyse, une comparaison ou les actions prioritaires…"
                      className="resize-none text-sm"
                      onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); envoyer(saisie); } }} />
            <Button size="icon" onClick={() => envoyer(saisie)} disabled={encours || !agent || !saisie.trim()}
                    className="bg-violet-600 hover:bg-violet-700">
              {encours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
            <MessageSquare className="w-3 h-3" /> L’agent analyse et conseille. Les campagnes ne sont jamais modifiées depuis cette conversation.
          </div>
        </footer>
      </section>
    </div>
  );
}
